import React, { useEffect, useRef, useState } from 'react';
import { useContent } from '../contexts/ContentContext';
import { GuideHelpId } from '../utils/guideHelp';
import Modal from './Modal';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    url: string;
    helpSection?: GuideHelpId;
}

export default function ShareModal({ isOpen, onClose, url, helpSection = 'image-share' }: ShareModalProps) {
    const { t } = useContent();
    const [copyState, setCopyState] = useState<'idle' | 'pending' | 'copied' | 'error'>('idle');
    const generation = useRef(0);
    const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        setCopyState('idle');
        return () => {
            generation.current += 1;
            if (resetTimer.current) clearTimeout(resetTimer.current);
        };
    }, [isOpen, url]);
    const copyUrl = async () => {
        const request = ++generation.current;
        if (resetTimer.current) clearTimeout(resetTimer.current);
        setCopyState('pending');
        try {
            await navigator.clipboard.writeText(url);
            if (request !== generation.current) return;
            setCopyState('copied');
            resetTimer.current = setTimeout(() => setCopyState('idle'), 2000);
        } catch {
            if (request === generation.current) setCopyState('error');
        }
    };
    return <Modal isOpen={isOpen} helpSection={helpSection} onClose={onClose} title={t('detail.text6')} panelClassName="max-w-xl">
        <div className="p-6 space-y-6">
            {copyState === 'error' && <p role="alert" className="lab-error">{t('detail.copyError')}</p>}
            <div className="flex flex-wrap gap-3">
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full bg-black text-white text-sm font-semibold hover:bg-gray-800 transition-colors">{t('detail.x')}</a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors">{t('detail.facebook')}</a>
                <a href={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-colors">{t('detail.line')}</a>
            </div>
            <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{t('detail.text7')}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 break-all select-text">{url}</div>
                    <button type="button" onClick={copyUrl} disabled={copyState === 'pending'} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors">{copyState === 'copied' ? t('detail.text8') : t('detail.text9')}</button>
                </div>
            </div>
        </div>
    </Modal>;
}
